import { useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { DataTable } from '../components/DataTable.tsx';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Dialog, EmptyState, Skeleton, StatusBadge, TableWrap } from '../components/ui.tsx';
import { locale, t, tn } from '../i18n/index.ts';
import { clock, dateTime, day, money, qty, todayIso } from '../lib/format.ts';
import { usePlaces } from '../lib/places.tsx';
import type { PayMethod } from '../lib/types.ts';
import { native } from '../lib/native.ts';
import { useAction } from './manage/common.tsx';
import { ReportTable, type ShiftReport } from './Shift.tsx';

interface Totals {
  sales: number;
  tax: number;
  net: number;
  orders: number;
  guests: number;
  average: number;
  discounts: number;
  tips: number;
  refunds?: number;
  refunds_total?: number;
}
interface Dashboard {
  period: { from: string; to: string; days: number; time_zone: string };
  previous_period: { from: string; to: string; until: string | null };
  totals: Totals;
  previous: Totals;
  by_day: { day: string; sales: number; orders: number; guests: number }[];
  by_hour: { hour: number; sales: number; orders: number }[];
  payments: { method: PayMethod; amount: number; count: number }[];
  channels: { channel: 'dine_in' | 'takeaway' | 'counter' | 'room' | 'shop' | 'rental'; sales: number; orders: number }[];
  products: { product: string; name: string; category: string | null; qty: number; sales: number; orders: number }[];
  categories: { id: string | null; name: string; tone: string | null; qty: number; sales: number }[];
  staff: { user: string | null; name: string; orders: number; sales: number; guests: number; tips: number; discounts: number }[];
  voids: { lines: number; value: number; cancelled_orders: number; cancelled_value: number; reasons: { reason: string | null; lines: number; value: number }[] };
  cost: { cost: number; net: number; orders: number; covered: number; percent: number | null } | null;
  location?: string | null;
  /** Each location side by side, for the whole business when there is more than one. */
  by_location?: ByPlace[];
}
interface ByPlace {
  location: string;
  name: string;
  code: string;
  active: boolean;
  sales: number;
  net: number;
  orders: number;
  guests: number;
  average: number;
  previous_sales: number;
  share: number;
}
interface ZShift {
  id: string;
  z_number: number;
  opened_at: string;
  closed_at: string;
  opening_cash: number;
  expected_cash: number;
  counted_cash: number;
  difference: number;
  note: string | null;
  report: ShiftReport;
  register: string;
  opened_by: string | null;
  closed_by: string | null;
  location_name?: string | null;
  location_code?: string | null;
}
interface ZList {
  shifts: ZShift[];
  totals: { shifts: number; sales: number; orders: number; difference: number; short: number; over: number };
}

type Preset = 'today' | 'yesterday' | 'week' | 'month' | 'last_month' | 'custom';
type Tab = 'overview' | 'items' | 'staff' | 'shifts';
const METHOD_ICON: Record<PayMethod, IconName> = { cash: 'cash', card: 'card', other: 'receipt', gift_card: 'gift', points: 'star' };
const PRESETS: Preset[] = ['today', 'yesterday', 'week', 'month', 'last_month', 'custom'];
const TABS: { key: Tab; icon: IconName }[] = [
  { key: 'overview', icon: 'chart' },
  { key: 'items', icon: 'receipt' },
  { key: 'staff', icon: 'users' },
  { key: 'shifts', icon: 'register' },
];

const shift = (d: string, n: number) => {
  const x = new Date(`${d}T00:00:00Z`);
  x.setUTCDate(x.getUTCDate() + n);
  return x.toISOString().slice(0, 10);
};
function presetRange(p: Exclude<Preset, 'custom'>, today: string): { from: string; to: string } {
  const month = today.slice(0, 8);
  switch (p) {
    case 'today':
      return { from: today, to: today };
    case 'yesterday':
      return { from: shift(today, -1), to: shift(today, -1) };
    case 'week':
      return { from: shift(today, -6), to: today };
    case 'month':
      return { from: `${month}01`, to: today };
    case 'last_month': {
      const end = shift(`${month}01`, -1);
      return { from: `${end.slice(0, 8)}01`, to: end };
    }
  }
}
export const isDay = (s: string | null): s is string => !!s && /^\d{4}-\d{2}-\d{2}$/.test(s);

/** Change against the period before, as a whole percent, or null when there is nothing to compare. */
const change = (now: number, before: number) => (before > 0 ? Math.round(((now - before) / before) * 100) : null);
const share = (part: number, whole: number) => (whole > 0 ? Math.round((part / whole) * 1000) / 10 : 0);
const pct = (n: number) => `${Number.isInteger(n) ? n : n.toFixed(1)}%`;

/** A spreadsheet file of a table, opened by Excel and others. */
export function downloadCsv(name: string, header: string[], rows: (string | number)[][]) {
  const cell = (v: string | number) => {
    const s = String(v);
    // A leading = + - @ would run as a formula in a spreadsheet.
    const safe = /^[=+\-@]/.test(s) && typeof v === 'string' ? `'${s}` : s;
    return /[",\n;]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
  };
  const text = [header, ...rows].map((r) => r.map(cell).join(',')).join('\r\n');
  // In the phone app the file goes to the phone's Downloads; a browser downloads it itself.
  if (native.saveFile(name, 'text/csv', `\ufeff${text}`)) return;
  const url = URL.createObjectURL(new Blob([`﻿${text}`], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
export const plain = (minor: number) => (minor / 100).toFixed(2);

/** How the business sold: totals against the period before, when, what, who, and the closed shifts. */
export function Reports() {
  const today = todayIso();
  const [params, setParams] = useSearchParams();
  const preset = (PRESETS.includes(params.get('p') as Preset) ? params.get('p') : 'week') as Preset;
  const tab = (TABS.some((x) => x.key === params.get('tab')) ? params.get('tab') : 'overview') as Tab;
  const custom = { from: isDay(params.get('from')) ? params.get('from')! : shift(today, -6), to: isDay(params.get('to')) ? params.get('to')! : today };
  const range = preset === 'custom' ? custom : presetRange(preset, today);
  const valid = range.from <= range.to;
  const places = usePlaces();
  // One location, or the whole business (also when a link names a location that no longer exists).
  const loc = places.multi && places.all.some((p) => p.id === params.get('loc')) ? params.get('loc')! : '';

  const set = (next: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v === null) p.delete(k);
      else p.set(k, v);
    }
    setParams(p, { replace: true });
  };

  return (
    <Shell>
      <div className="page reports">
        <div className="page-head">
          <div>
            <h1>{t('rep.title')}</h1>
            <p className="muted">
              {range.from === range.to ? day(range.from) : t('rep.range', { from: day(range.from), to: day(range.to) })}
              {places.multi ? ` · ${loc ? places.name(loc) : t('places.all')}` : ''}
            </p>
          </div>
        </div>

        <div className="toolbar">
          <div className="toolbar-group">
            <div className="segmented" role="radiogroup" aria-label={t('rep.period')}>
              {PRESETS.map((p) => (
                <button
                  key={p}
                  role="radio"
                  aria-checked={preset === p}
                  className={preset === p ? 'active' : ''}
                  onClick={() => set(p === 'custom' ? { p, from: range.from, to: range.to } : { p: p === 'week' ? null : p, from: null, to: null })}
                >
                  {t(`rep.p_${p}`)}
                </button>
              ))}
            </div>
            {preset === 'custom' ? (
              <div className="rep-dates">
                <input
                  className="input date-input"
                  type="date"
                  aria-label={t('rep.from')}
                  value={range.from}
                  max={today}
                  onChange={(e) => isDay(e.target.value) && set({ from: e.target.value })}
                />
                <span className="muted" aria-hidden="true">
                  –
                </span>
                <input
                  className="input date-input"
                  type="date"
                  aria-label={t('rep.to')}
                  value={range.to}
                  max={today}
                  onChange={(e) => isDay(e.target.value) && set({ to: e.target.value })}
                />
              </div>
            ) : null}
          </div>
          {places.multi ? (
            <select className="input place-select" value={loc} onChange={(e) => set({ loc: e.target.value || null })} aria-label={t('places.location')}>
              <option value="">{t('places.all')}</option>
              {places.all.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.active ? p.name : t('places.closed_name', { name: p.name })}
                </option>
              ))}
            </select>
          ) : null}
        </div>

        <div className="segmented tabs" role="tablist" aria-label={t('rep.title')}>
          {TABS.map((x) => (
            <button key={x.key} role="tab" aria-selected={tab === x.key} className={tab === x.key ? 'active' : ''} onClick={() => set({ tab: x.key === 'overview' ? null : x.key })}>
              <Icon name={x.icon} size={16} />
              {t(`rep.tab_${x.key}`)}
            </button>
          ))}
        </div>

        {!valid ? (
          <EmptyState icon="alert" title={t('rep.bad_range')} body={t('rep.bad_range_body')} />
        ) : tab === 'shifts' ? (
          <Shifts from={range.from} to={range.to} loc={loc} />
        ) : (
          <SalesTabs tab={tab} from={range.from} to={range.to} loc={loc} onTab={(k) => set({ tab: k })} onPlace={(id) => set({ loc: id })} />
        )}
      </div>
    </Shell>
  );
}

function SalesTabs({
  tab,
  from,
  to,
  loc,
  onTab,
  onPlace,
}: {
  tab: Exclude<Tab, 'shifts'>;
  from: string;
  to: string;
  loc: string;
  onTab: (t: Tab) => void;
  onPlace: (id: string) => void;
}) {
  const { data } = useAction<Dashboard>('reports.dashboard', { from, to, ...(loc ? { location: loc } : {}) });
  if (!data || data.period.from !== from || data.period.to !== to || (data.location ?? '') !== loc) {
    return (
      <div className="rep-grid">
        <Skeleton h={112} r="var(--radius-lg)" />
        <Skeleton h={300} r="var(--radius-lg)" />
      </div>
    );
  }
  if (tab === 'items') return <Items d={data} />;
  if (tab === 'staff') return <Staff d={data} />;
  return <Overview d={data} onTab={onTab} onPlace={onPlace} />;
}

/* ---------------------------------------------------------------- Overview */

function Overview({ d, onTab, onPlace }: { d: Dashboard; onTab: (t: Tab) => void; onPlace: (id: string) => void }) {
  const one = d.period.days === 1;
  const prevLabel = d.previous_period.from === d.previous_period.to ? day(d.previous_period.from) : t('rep.range', { from: day(d.previous_period.from), to: day(d.previous_period.to) });
  if (d.totals.orders === 0 && d.voids.cancelled_orders === 0) {
    return <EmptyState icon="chart" title={t('rep.no_sales')} body={t('rep.no_sales_body')} />;
  }
  const busiest = [...d.by_hour].sort((a, b) => b.sales - a.sales)[0];
  const hours = d.by_hour.filter((h) => h.hour >= firstHour(d) && h.hour <= lastHour(d));
  return (
    <div className="rep-grid">
      <section className="rep-kpis" aria-label={t('rep.totals')}>
        <Kpi label={t('rep.k_sales')} value={money(d.totals.sales)} delta={change(d.totals.sales, d.previous.sales)} hint={t('rep.vat_in', { tax: money(d.totals.tax) })} />
        <Kpi label={t('rep.k_orders')} value={String(d.totals.orders)} delta={change(d.totals.orders, d.previous.orders)} hint={tn('rep.guests', d.totals.guests)} />
        <Kpi label={t('rep.k_average')} value={money(d.totals.average)} delta={change(d.totals.average, d.previous.average)} hint={t('rep.per_bill')} />
        {d.cost ? (
          <Kpi
            label={t('rep.k_food_cost')}
            value={d.cost.percent === null ? '—' : pct(d.cost.percent)}
            hint={
              !d.cost.covered ? t('rep.food_cost_none') : d.cost.covered < d.cost.orders ? t('rep.food_cost_partial', { n: d.cost.covered, total: d.cost.orders }) : t('rep.food_cost_hint', { cost: money(d.cost.cost) })
            }
            warn={d.cost.covered > 0 && d.cost.covered < d.cost.orders}
          />
        ) : (
          <Kpi label={t('rep.k_tips')} value={money(d.totals.tips)} delta={change(d.totals.tips, d.previous.tips)} />
        )}
      </section>
      <p className="muted rep-compare">
        {d.previous_period.until ? t('rep.compared_until', { period: prevLabel, time: d.previous_period.until }) : t('rep.compared', { period: prevLabel })}
      </p>

      {d.by_location?.length ? <ByLocation rows={d.by_location} onPlace={onPlace} /> : null}

      <section className="panel rep-wide" aria-labelledby="rep-trend">
        <div className="panel-head">
          <h2 id="rep-trend">{one ? t('rep.by_hour') : t('rep.by_day')}</h2>
          <span className="muted">{one ? t('rep.busiest', { hour: hourLabel(busiest!.hour) }) : t('rep.best_day', { day: day(bestDay(d).day), total: money(bestDay(d).sales) })}</span>
        </div>
        {one ? (
          <Bars items={hours.map((h) => ({ key: String(h.hour), label: hourLabel(h.hour), value: h.sales, tip: t('rep.tip_hour', { hour: hourLabel(h.hour), total: money(h.sales), n: h.orders }) }))} />
        ) : (
          <Bars
            items={d.by_day.map((x) => ({
              key: x.day,
              label: dayShort(x.day, d.by_day.length),
              value: x.sales,
              tip: t('rep.tip_day', { day: day(x.day), total: money(x.sales), n: x.orders }),
              weekend: isWeekend(x.day),
            }))}
          />
        )}
      </section>

      {!one ? (
        <section className="panel" aria-labelledby="rep-hours">
          <div className="panel-head">
            <h2 id="rep-hours">{t('rep.by_hour')}</h2>
            <span className="muted">{t('rep.busiest', { hour: hourLabel(busiest!.hour) })}</span>
          </div>
          <Bars small items={hours.map((h) => ({ key: String(h.hour), label: hourLabel(h.hour), value: h.sales, tip: t('rep.tip_hour', { hour: hourLabel(h.hour), total: money(h.sales), n: h.orders }) }))} />
        </section>
      ) : null}

      <section className="panel" aria-labelledby="rep-pay">
        <div className="panel-head">
          <h2 id="rep-pay">{t('rep.payments')}</h2>
          <span className="muted">{t('rep.with_tips', { tips: money(d.totals.tips) })}</span>
        </div>
        <Shares
          rows={d.payments.map((p) => ({
            key: p.method,
            icon: METHOD_ICON[p.method],
            label: t(`pay.method_${p.method}`),
            value: p.amount,
            detail: tn('rep.payments_n', p.count),
          }))}
        />
        {d.channels.length > 1 ? (
          <>
            <h3 className="rep-sub">{t('rep.channels')}</h3>
            <Shares
              rows={d.channels.map((c) => ({
                key: c.channel,
                icon: c.channel === 'dine_in' ? 'table' : c.channel === 'room' ? 'bed' : c.channel === 'shop' ? 'cart' : c.channel === 'rental' ? 'key' : 'bag',
                label: t(`rep.ch_${c.channel}`),
                value: c.sales,
                detail: tn('rep.orders_n', c.orders),
              }))}
            />
          </>
        ) : null}
      </section>

      <section className="panel" aria-labelledby="rep-top">
        <div className="panel-head">
          <h2 id="rep-top">{t('rep.top_items')}</h2>
          <Button variant="ghost" size="sm" onClick={() => onTab('items')}>
            {t('rep.see_all')}
          </Button>
        </div>
        <Shares rows={d.products.slice(0, 6).map((p) => ({ key: p.product, label: p.name, value: p.sales, detail: t('rep.sold_qty', { n: qty(p.qty) }) }))} total={d.totals.sales} />
      </section>

      <section className="panel" aria-labelledby="rep-staff">
        <div className="panel-head">
          <h2 id="rep-staff">{t('rep.staff')}</h2>
          <Button variant="ghost" size="sm" onClick={() => onTab('staff')}>
            {t('rep.see_all')}
          </Button>
        </div>
        <Shares rows={d.staff.slice(0, 5).map((s) => ({ key: s.user ?? '-', icon: 'users', label: s.name || t('rep.unknown'), value: s.sales, detail: tn('rep.orders_n', s.orders) }))} total={d.totals.sales} />
      </section>

      <section className="panel" aria-labelledby="rep-loss">
        <div className="panel-head">
          <h2 id="rep-loss">{t('rep.losses')}</h2>
        </div>
        <dl className="report">
          <dt>{t('rep.discounts')}</dt>
          <dd>{money(d.totals.discounts)}</dd>
          <dt>{tn('rep.refunds', d.totals.refunds ?? 0)}</dt>
          <dd>{money(d.totals.refunds_total ?? 0)}</dd>
          <dt>{tn('rep.void_lines', d.voids.lines)}</dt>
          <dd>{money(d.voids.value)}</dd>
          <dt>{tn('rep.cancelled_orders', d.voids.cancelled_orders)}</dt>
          <dd>{money(d.voids.cancelled_value)}</dd>
        </dl>
        {d.voids.reasons.length ? (
          <>
            <h3 className="rep-sub">{t('rep.void_reasons')}</h3>
            <ul className="rep-reasons">
              {d.voids.reasons.map((r) => (
                <li key={r.reason ?? '-'}>
                  <span>{r.reason ?? t('rep.no_reason')}</span>
                  <span className="muted">
                    {tn('rep.lines_n', r.lines)} · {money(r.value)}
                  </span>
                </li>
              ))}
            </ul>
          </>
        ) : null}
      </section>

      {d.cost ? (
        <section className="panel" aria-labelledby="rep-cost">
          <div className="panel-head">
            <h2 id="rep-cost">{t('rep.food_cost')}</h2>
          </div>
          {d.cost.covered ? (
            <dl className="report">
              <dt>{t('rep.cost_net')}</dt>
              <dd>{money(d.cost.net)}</dd>
              <dt>{t('rep.cost_used')}</dt>
              <dd>{money(d.cost.cost)}</dd>
              <dt className="strong">{t('rep.cost_margin')}</dt>
              <dd className="strong">{money(d.cost.net - d.cost.cost)}</dd>
            </dl>
          ) : null}
          <p className={`rep-note${d.cost.covered < d.cost.orders ? ' warn' : ''}`}>
            <Icon name={d.cost.covered < d.cost.orders ? 'alert' : 'check'} size={16} />
            <span>
              {d.cost.covered < d.cost.orders ? t('rep.cost_partial', { n: d.cost.covered, total: d.cost.orders }) : t('rep.cost_full')}
            </span>
          </p>
        </section>
      ) : null}
    </div>
  );
}

/** Each location next to the others: what it sold, against the period before, and its share. */
function ByLocation({ rows, onPlace }: { rows: ByPlace[]; onPlace: (id: string) => void }) {
  return (
    <section className="panel rep-wide" aria-labelledby="rep-places">
      <div className="panel-head">
        <h2 id="rep-places">{t('rep.by_location')}</h2>
        <span className="muted">{t('rep.by_location_hint')}</span>
      </div>
      <ul className="rep-places">
        {rows.map((r) => {
          const delta = change(r.sales, r.previous_sales);
          return (
            <li key={r.location}>
              <button type="button" className="rep-place" onClick={() => onPlace(r.location)} aria-label={t('rep.open_location', { name: r.name })}>
                <span className="place-badge" aria-hidden="true">
                  {r.code}
                </span>
                <span className="rep-place-main">
                  <span className="strong">
                    {r.name}
                    {r.active ? null : <span className="muted"> · {t('locations.closed')}</span>}
                  </span>
                  <span className="muted small">
                    {tn('rep.orders_n', r.orders)} · {t('rep.avg_bill', { amount: money(r.average) })}
                  </span>
                  <span className="share-bar" aria-hidden="true">
                    <span style={{ width: `${Math.min(100, r.share)}%` }} />
                  </span>
                </span>
                <span className="rep-place-end">
                  <span className="strong">{money(r.sales)}</span>
                  <span className="rep-place-foot">
                    {delta === null ? (
                      <span className="muted">{t('rep.no_compare')}</span>
                    ) : (
                      <span className={`rep-delta ${delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat'}`}>
                        <Icon name={delta > 0 ? 'trendUp' : delta < 0 ? 'trendDown' : 'minus'} size={14} />
                        {delta > 0 ? '+' : ''}
                        {delta}%
                      </span>
                    )}
                    <span className="muted">{pct(r.share)}</span>
                  </span>
                </span>
                <Icon name="chevronRight" size={18} />
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export function Kpi({ label, value, delta, hint, warn }: { label: string; value: string; delta?: number | null; hint?: string; warn?: boolean }) {
  return (
    <div className="rep-kpi">
      <span className="rep-kpi-label">{label}</span>
      <span className="rep-kpi-value">{value}</span>
      <span className="rep-kpi-foot">
        {delta === undefined ? null : delta === null ? (
          <span className="muted">{t('rep.no_compare')}</span>
        ) : (
          <span className={`rep-delta ${delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat'}`}>
            <Icon name={delta > 0 ? 'trendUp' : delta < 0 ? 'trendDown' : 'minus'} size={14} />
            {delta > 0 ? '+' : ''}
            {delta}%
          </span>
        )}
        {hint ? (
          warn ? (
            <span className="rep-warn">
              <Icon name="alert" size={14} />
              {hint}
            </span>
          ) : (
            <span className="muted">{hint}</span>
          )
        ) : null}
      </span>
    </div>
  );
}

/** Bars from the theme colours; every bar says its value when pointed at, and screen readers get a table. */
export function Bars({ items, small }: { items: { key: string; label: string; value: number; tip: string; weekend?: boolean }[]; small?: boolean }) {
  const max = Math.max(1, ...items.map((i) => i.value));
  const every = Math.ceil(items.length / (small ? 8 : 12));
  return (
    <figure className={`bars${small ? ' small' : ''}`}>
      <div className="bars-plot" aria-hidden="true" style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}>
        {items.map((i) => (
          <span key={i.key} className="bar-col" data-tip={i.tip}>
            <span className={`bar${i.weekend ? ' alt' : ''}`} style={{ height: `${Math.max(i.value ? 2 : 0, (i.value / max) * 100)}%` }} />
          </span>
        ))}
      </div>
      <div className="bars-axis" aria-hidden="true" style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}>
        {items.map((i, n) => (
          <span key={i.key}>{n % every === 0 ? i.label : ''}</span>
        ))}
      </div>
      <table className="sr-only">
        <tbody>
          {items.map((i) => (
            <tr key={i.key}>
              <td>{i.tip}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}

/** Rows with a bar showing each one's share of the whole. */
function Shares({ rows, total }: { rows: { key: string; icon?: IconName; label: string; value: number; detail?: string }[]; total?: number }) {
  const whole = total ?? rows.reduce((a, r) => a + r.value, 0);
  if (!rows.length) return <p className="muted">{t('rep.nothing')}</p>;
  return (
    <ul className="shares">
      {rows.map((r) => (
        <li key={r.key}>
          <div className="share-line">
            <span className="share-name">
              {r.icon ? <Icon name={r.icon} size={16} /> : null}
              <span>{r.label}</span>
            </span>
            <span className="share-value">{money(r.value)}</span>
          </div>
          <div className="share-bar" aria-hidden="true">
            <span style={{ width: `${Math.min(100, share(r.value, whole))}%` }} />
          </div>
          <div className="share-foot muted">
            <span>{r.detail}</span>
            <span>{pct(share(r.value, whole))}</span>
          </div>
        </li>
      ))}
    </ul>
  );
}

/* ---------------------------------------------------------------- Items */

function Items({ d }: { d: Dashboard }) {
  const [by, setBy] = useState<'items' | 'categories'>('items');
  const [search, setSearch] = useState('');
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return d.products.filter((p) => !q || p.name.toLowerCase().includes(q) || (p.category ?? '').toLowerCase().includes(q));
  }, [d.products, search]);
  const file = `${by}-${d.period.from}-${d.period.to}.csv`;
  const download = () =>
    by === 'items'
      ? downloadCsv(file, [t('rep.c_item'), t('rep.c_category'), t('rep.c_qty'), t('rep.c_orders'), t('rep.c_sales'), t('rep.c_share')], d.products.map((p) => [p.name, p.category ?? '', p.qty, p.orders, plain(p.sales), share(p.sales, d.totals.sales)]))
      : downloadCsv(file, [t('rep.c_category'), t('rep.c_qty'), t('rep.c_sales'), t('rep.c_share')], d.categories.map((c) => [c.name || t('rep.no_category'), c.qty, plain(c.sales), share(c.sales, d.totals.sales)]));
  if (!d.products.length) return <EmptyState icon="receipt" title={t('rep.no_sales')} body={t('rep.no_sales_body')} />;
  return (
    <section className="list-card" aria-label={t('rep.tab_items')}>
      <div className="list-card-head">
        <div className="toolbar-group">
          <div className="segmented subtle" role="radiogroup" aria-label={t('rep.group_by')}>
            {(['items', 'categories'] as const).map((k) => (
              <button key={k} role="radio" aria-checked={by === k} className={by === k ? 'active' : ''} onClick={() => setBy(k)}>
                {t(`rep.by_${k}`)}
              </button>
            ))}
          </div>
          {by === 'items' ? (
            <input className="input rep-search" type="search" placeholder={t('rep.search_items')} aria-label={t('rep.search_items')} value={search} onChange={(e) => setSearch(e.target.value)} />
          ) : null}
        </div>
        <Button variant="secondary" size="sm" icon="download" onClick={download}>
          {t('rep.download')}
        </Button>
      </div>
      <TableWrap>
        {by === 'items' ? (
          <Table
            head={[t('rep.c_item'), t('rep.c_qty'), t('rep.c_orders'), t('rep.c_sales'), t('rep.c_share')]}
            rows={rows.map((p) => ({
              key: p.product,
              cells: [
                <span key="n" className="cell-main">
                  <span className="strong">{p.name}</span>
                  <span className="muted">{p.category ?? t('rep.no_category')}</span>
                </span>,
                qty(p.qty),
                String(p.orders),
                money(p.sales),
                <ShareCell key="s" value={share(p.sales, d.totals.sales)} />,
              ],
            }))}
            foot={search ? null : [t('rep.total'), '', String(d.totals.orders), money(d.totals.sales), '100%']}
            empty={t('rep.no_match')}
            phoneHide={[2, 4]}
          />
        ) : (
          <Table
            head={[t('rep.c_category'), t('rep.c_qty'), t('rep.c_sales'), t('rep.c_share')]}
            rows={d.categories.map((c) => ({
              key: c.id ?? '-',
              cells: [
                <span key="n" className="cell-main">
                  <span className={`cat-swatch tone-${c.tone ?? 'grey'}`} aria-hidden="true" />
                  <span className="strong">{c.name || t('rep.no_category')}</span>
                </span>,
                qty(c.qty),
                money(c.sales),
                <ShareCell key="s" value={share(c.sales, d.totals.sales)} />,
              ],
            }))}
            foot={[t('rep.total'), '', money(d.totals.sales), '100%']}
            phoneHide={[1]}
          />
        )}
      </TableWrap>
    </section>
  );
}

function ShareCell({ value }: { value: number }) {
  return (
    <span className="share-cell">
      <span className="share-bar" aria-hidden="true">
        <span style={{ width: `${Math.min(100, value)}%` }} />
      </span>
      <span>{pct(value)}</span>
    </span>
  );
}

/** The app's table now lives in components/DataTable.tsx; screens keep importing it from here. */
export const Table = DataTable;

/* ---------------------------------------------------------------- Staff */

function Staff({ d }: { d: Dashboard }) {
  if (!d.staff.length) return <EmptyState icon="users" title={t('rep.no_sales')} body={t('rep.no_sales_body')} />;
  const download = () =>
    downloadCsv(
      `staff-${d.period.from}-${d.period.to}.csv`,
      [t('rep.c_person'), t('rep.c_orders'), t('rep.c_guests'), t('rep.c_sales'), t('rep.c_average'), t('rep.c_tips'), t('rep.c_discounts')],
      d.staff.map((s) => [s.name, s.orders, s.guests, plain(s.sales), plain(s.orders ? Math.round(s.sales / s.orders) : 0), plain(s.tips), plain(s.discounts)]),
    );
  return (
    <section className="list-card" aria-label={t('rep.tab_staff')}>
      <div className="list-card-head">
        <div>
          <h2>{t('rep.staff')}</h2>
          <p className="muted">{t('rep.staff_hint')}</p>
        </div>
        <Button variant="secondary" size="sm" icon="download" onClick={download}>
          {t('rep.download')}
        </Button>
      </div>
      <TableWrap>
        <Table
          head={[t('rep.c_person'), t('rep.c_orders'), t('rep.c_guests'), t('rep.c_sales'), t('rep.c_average'), t('rep.c_tips'), t('rep.c_discounts')]}
          rows={d.staff.map((s) => ({
            key: s.user ?? '-',
            cells: [
              <span key="n" className="cell-main">
                <span className="avatar sm" aria-hidden="true">
                  {initials(s.name)}
                </span>
                <span className="strong">{s.name || t('rep.unknown')}</span>
              </span>,
              String(s.orders),
              String(s.guests),
              money(s.sales),
              money(s.orders ? Math.round(s.sales / s.orders) : 0),
              money(s.tips),
              money(s.discounts),
            ],
          }))}
          foot={[t('rep.total'), String(d.totals.orders), String(d.totals.guests), money(d.totals.sales), money(d.totals.average), money(d.totals.tips), money(d.totals.discounts)]}
          phoneHide={[2, 5, 6]}
        />
      </TableWrap>
    </section>
  );
}

/* ---------------------------------------------------------------- Shifts */

function DiffBadge({ diff }: { diff: number }) {
  if (diff === 0) return <StatusBadge tone="ok" icon="check" label={t('rep.balanced')} />;
  if (diff < 0) return <StatusBadge tone="danger" icon="alert" label={t('rep.short', { amount: money(-diff) })} />;
  return <StatusBadge tone="warn" icon="alert" label={t('rep.over', { amount: money(diff) })} />;
}

function Shifts({ from, to, loc }: { from: string; to: string; loc: string }) {
  const { data } = useAction<ZList>('reports.z_reports', { from, to, ...(loc ? { location: loc } : {}) });
  const { multi } = usePlaces();
  const [open, setOpen] = useState<ZShift | null>(null);
  if (!data) return <Skeleton h={300} r="var(--radius-lg)" />;
  const download = () =>
    downloadCsv(
      `z-reports-${from}-${to}.csv`,
      [t('rep.c_z'), t('rep.c_location'), t('rep.c_register'), t('rep.c_opened'), t('rep.c_closed'), t('rep.c_closed_by'), t('rep.c_orders'), t('rep.c_sales'), t('rep.c_expected'), t('rep.c_counted'), t('rep.c_difference'), t('rep.c_note')],
      data.shifts.map((s) => [
        s.z_number,
        s.location_name ?? '',
        s.register,
        dateTime(s.opened_at),
        dateTime(s.closed_at),
        s.closed_by ?? '',
        s.report.orders,
        plain(s.report.sales_total),
        plain(s.expected_cash),
        plain(s.counted_cash),
        plain(s.difference),
        s.note ?? '',
      ]),
    );
  return (
    <>
      <dl className="fiscal-stats">
        <div className="fiscal-stat">
          <dt>{t('rep.k_shifts')}</dt>
          <dd>{data.totals.shifts}</dd>
        </div>
        <div className="fiscal-stat">
          <dt>{t('rep.k_shift_sales')}</dt>
          <dd>{money(data.totals.sales)}</dd>
        </div>
        <div className={`fiscal-stat${data.totals.short ? ' danger' : ''}`}>
          <dt>{t('rep.k_short')}</dt>
          <dd>{data.totals.short}</dd>
        </div>
        <div className={`fiscal-stat${data.totals.difference < 0 ? ' danger' : data.totals.difference > 0 ? ' warn' : ''}`}>
          <dt>{t('rep.k_difference')}</dt>
          <dd>{money(data.totals.difference, { sign: true })}</dd>
        </div>
      </dl>
      <section className="list-card" aria-label={t('rep.tab_shifts')}>
        <div className="list-card-head">
          <div>
            <h2>{t('rep.z_title')}</h2>
            <p className="muted">{t('rep.z_hint')}</p>
          </div>
          {data.shifts.length ? (
            <Button variant="secondary" size="sm" icon="download" onClick={download}>
              {t('rep.download')}
            </Button>
          ) : null}
        </div>
        {data.shifts.length === 0 ? (
          <EmptyState icon="register" title={t('rep.no_shifts')} body={t('rep.no_shifts_body')} />
        ) : (
          data.shifts.map((s) => (
            <button key={s.id} className="list-row stock-row" onClick={() => setOpen(s)}>
              <span className="list-icon" aria-hidden="true">
                <Icon name="register" size={18} />
              </span>
              <span className="list-main">
                <span className="strong">
                  Z-{s.z_number} · {s.register}
                  {multi && !loc && s.location_code ? <span className="place-tag">{s.location_code}</span> : null}
                </span>
                <span className="muted">
                  {dateDay(s.closed_at) === dateDay(s.opened_at)
                    ? t('rep.shift_times', { day: dateDay(s.closed_at), from: clock(s.opened_at), to: clock(s.closed_at) })
                    : t('rep.shift_span', { from: dateTime(s.opened_at), to: dateTime(s.closed_at) })}
                  {s.closed_by ? ` · ${s.closed_by}` : ''}
                </span>
              </span>
              <span className="row-end">
                <span className="strong">{money(s.report.sales_total)}</span>
                <DiffBadge diff={s.difference} />
              </span>
            </button>
          ))
        )}
      </section>
      {open ? (
        <Dialog title={t('rep.z_dialog', { n: open.z_number })} onClose={() => setOpen(null)}>
          <p className="muted">
            {multi && open.location_name ? `${open.location_name} · ` : ''}
            {open.register} · {dateTime(open.opened_at)} – {dateTime(open.closed_at)}
          </p>
          <ReportTable report={open.report} opening={open.opening_cash} />
          <dl className="report">
            <dt>{t('shift.counted')}</dt>
            <dd>{money(open.counted_cash)}</dd>
            <dt className="strong">{t('shift.difference')}</dt>
            <dd className={`strong ${open.difference < 0 ? 'text-danger' : open.difference > 0 ? 'text-warn' : 'text-ok'}`}>{money(open.difference, { sign: true })}</dd>
          </dl>
          {open.note ? <p className="rep-note warn">{open.note}</p> : null}
          <p className="muted">{t('rep.z_people', { opened: open.opened_by ?? '—', closed: open.closed_by ?? '—' })}</p>
        </Dialog>
      ) : null}
    </>
  );
}

/* ---------------------------------------------------------------- helpers */

const hourLabel = (h: number) => `${String(h).padStart(2, '0')}:00`;
const dateDay = (at: string) => new Intl.DateTimeFormat(locale(), { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(at));
const isWeekend = (d: string) => [0, 6].includes(new Date(`${d}T12:00:00Z`).getUTCDay());
function dayShort(d: string, count: number): string {
  const date = new Date(`${d}T12:00:00Z`);
  if (count <= 7) return new Intl.DateTimeFormat(locale(), { weekday: 'short', timeZone: 'UTC' }).format(date);
  return new Intl.DateTimeFormat(locale(), { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(date);
}
const bestDay = (d: Dashboard) => [...d.by_day].sort((a, b) => b.sales - a.sales)[0]!;
/** Opening hours as the sales show them, so the hour chart does not start with a long empty night. */
function firstHour(d: Dashboard) {
  const h = d.by_hour.find((x) => x.orders > 0)?.hour ?? 8;
  return Math.min(h, 8);
}
function lastHour(d: Dashboard) {
  const h = [...d.by_hour].reverse().find((x) => x.orders > 0)?.hour ?? 22;
  return Math.max(h, 22);
}
const initials = (name: string) =>
  name
    .split(/\s+/)
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || '?';

